/*
This file defines several Custom Elements for handling product interactions:
ProductForm: Adding a product to the cart.
VariantSelects: Base class for updating product variants based on user selection.
VariantRadios: Extends VariantSelects for handling variants selected via radio buttons.
ProductOptions: Handles clicks on product options, updating the price and variant ID.
CardVariantSelects: Extends VariantSelects, adapted for variant selection within product card elements.
CardSwatches: Extends CardVariantSelects, used for variant selection using 'swatches' in cards.
*/

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.forms = this.querySelectorAll('form:not(.back-in-stock)');

        this.forms.forEach(form => {
          const input = form.querySelector('[name=id]');

          if (input) input.disabled = false;
          form.addEventListener('submit', event => this.onSubmitHandler(event, form));
        });
      }

      // Handler for form submission (Add to Cart)
      onSubmitHandler(evt, form) {
        evt.preventDefault();

        const submitButton = form.querySelector('[type="submit"]');

        if (submitButton.getAttribute('aria-disabled') === 'true') return;
        this.handleErrorMessage();

        submitButton.setAttribute('aria-disabled', 'true');
        submitButton.classList.add('loading');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(form);
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then(response => response.json())
          .then(response => {
            console.log(response.status)

            if (response.status) {
              this.handleErrorMessage(response.description);

              if (response.message === 'Only 1 item was added to your cart due to availability.') {
                publish(PUB_SUB_EVENTS.cartAdd);
              }

              const soldOutMessage = submitButton.querySelector('.sold-out-message');

              if (!soldOutMessage) return;

              submitButton.setAttribute('aria-disabled', true);
              submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;

              return;
            }

            if (!this.error) publish(PUB_SUB_EVENTS.cartAdd);

            this.error = false;
          })
          .catch(e => {
            console.error(e)
          })
          .finally(() => {
            submitButton.classList.remove('loading');
            if (!this.error) submitButton.removeAttribute('aria-disabled');

            const addButtons = document.querySelectorAll('.js-add-button');

            if (addButtons.length) {
              addButtons.forEach(button => {
                button.classList.remove('loading');
              });
            }
          });
      }

      // Handles displaying or hiding the product form error message
      handleErrorMessage(errorMessage = false) {
        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) this.errorMessage.textContent = errorMessage;
      }
    }
  );
}

// Base class for handling product variant selection
class VariantSelects extends HTMLElement {
  constructor() {
    super();

    this.productSection = document.querySelector('.js-product-section');

    this.addEventListener('change', this.onVariantChange);
    this.addFakeListener();
    this.updateVariantImage();
  }

  onVariantChange() {
    this.updateOptions(); // Collect current selected options
    this.updateMasterId(); // Find the corresponding variant object
    this.toggleAddButton(true, false, true); // Set the Add to Cart button to a loading state
    this.removeErrorMessage(); // Clear previous error messages
    this.updateVariantStatuses(); // Update variant's status

    if (!this.currentVariant) {
      // If no variant is found
      this.toggleAddButton(true, true, false); // Disable button and set text to "Sold Out"
      this.setUnavailable(); // Set other product info elements to unavailable state
    } else {
      // Variant successfully found
      this.updateURL(); // Update the browser URL with the new variant ID
      this.updateVariantInput(); // Update the hidden variant ID input in the form
      this.renderProductInfo(); // Fetch and render new price, media, etc.
      this.updateVariantImage(); // Switch to variant's attached image
    }
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), select => select.value);
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find(variant => {
      return !variant.options
        .map((option, index) => this.options[index] === option)
        .includes(false);
    });
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;

    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput(forms) {
    const productForms =
      forms ?? document.querySelectorAll(`#product-form-${this.dataset.section}`);

    productForms.forEach(productForm => {
      const input = productForm.querySelector('input[name="id"]');
      const formInput = productForm.querySelector('input[name="quantity"]');

      formInput.value = 1;

      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    if (this.dataset.bundle)
      publish(PUB_SUB_EVENTS.bundleUpdate, { variant: this.currentVariant.id, element: this });
    if (!this.productSection) return;

    const addButtons = this.productSection.querySelectorAll('add-product');

    if (!addButtons.length) return;

    addButtons.forEach(button => button.setAttribute('data-id', this.currentVariant.id));
  }

  updateVariantStatuses() {
    const inputWrappers = [...this.querySelectorAll('.product-form__input')];

    inputWrappers.forEach((option, index) => {
      if (index === 0) return;

      const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
      const previousOptionSelected = inputWrappers[index - 1].querySelector(':checked').value;
      const availableOptionInputsValue = this.getAvailableOptions(previousOptionSelected, index);

      this.setInputAvailability(optionInputs, availableOptionInputsValue);
    });
  }

  getAvailableOptions(previousOptionSelected, index) {
    return this.getSelectedVariants()
      .filter(variant => variant.available && variant[`option${index}`] === previousOptionSelected)
      .map(variantOption => variantOption[`option${index + 1}`]);
  }

  getSelectedVariants() {
    return this.variantData.filter(
      variant => this.querySelector(':checked').value === variant.option1
    );
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach(input => {
      const value = input.getAttribute('value');
      const optionUnavailable = window.variantStrings.unavailable_with_option;

      input.innerText = listOfAvailableOptions.includes(value)
        ? value
        : optionUnavailable.replace('[value]', value);
    });
  }

  removeErrorMessage() {
    const section = this.closest('section');

    if (!section) return;

    const productForm = section.querySelector('product-form');

    if (productForm) productForm.handleErrorMessage();
  }

  renderProductInfo() {
    const requestedVariantId = this.currentVariant.id;
    const sectionId = this.dataset.originalSection
      ? this.dataset.originalSection
      : this.dataset.section;

    fetch(
      `${this.dataset.url}?variant=${requestedVariantId}&section_id=${
        this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section
      }`
    )
      .then(response => response.text())
      .then(responseText => {
        if (this.currentVariant.id !== requestedVariantId) return;

        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const section = document.getElementById(`shopify-section-${this.dataset.section}`);
        const quantityPrice = section.querySelector('.product__quantity-price');
        const newQuantityPrice = html.querySelector('.product__quantity-price');
        const newPrice = html.querySelector(`.price-${this.dataset.section}`);
        const prices = section.querySelectorAll(`.price-${this.dataset.section}`);
        const newSelects = html.querySelectorAll('.select');
        const selects = section.querySelectorAll('.select');
        const addButtonUpdated = html.getElementById(`ProductSubmitButton-${sectionId}`);
        const isDisabled = addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true;

        this.updateStickyBag(html, section);
        this.updateQuantityPrice(quantityPrice, newQuantityPrice);
        this.updatePrice(prices, newPrice);
        this.updateVariants(selects, newSelects);
        this.toggleAddButton(isDisabled);
      });
  }

  updateStickyBag(html, section) {
    const bag = section.querySelector('.js-sticky-bag');
    const newBag = html.querySelector('.js-sticky-bag');
    const quantity = bag.querySelector('.js-quantity-input');
    const newQuantity = newBag.querySelector('.js-quantity-input');
    const variant = bag.querySelector('.js-variant');
    const newVariant = newBag.querySelector('.js-variant');
    const image = bag.querySelector('.js-image');
    const newImage = newBag.querySelector('.js-image');

    this.updateBagImage(image, newImage);
    this.updateBagQuantity(quantity, newQuantity);
    this.updateBagVariant(variant, newVariant);
  }

  updateBagImage(bagImage, newBagImage) {
    if (bagImage && newBagImage) bagImage.src = newBagImage.src;
  }

  updateBagVariant(bagVariant, newBagVariant) {
    if (bagVariant && newBagVariant) bagVariant.innerHTML = newBagVariant.innerHTML;
  }

  updateQuantityPrice(quantityPrice, newQuantityPrice) {
    if (quantityPrice && newQuantityPrice) quantityPrice.innerHTML = newQuantityPrice.innerHTML;
  }

  updateBagQuantity(quantity, newQuantity) {
    if (quantity && newQuantity) quantity.innerHTML = newQuantity.innerHTML;

    quantity.value = 1;
  }

  updatePrice(prices, newPrice) {
    prices.forEach(priceElement => {
      if (!priceElement.dataset.bundle) priceElement.innerHTML = newPrice.innerHTML;
    });
  }

  updateVariants(selects, newSelects) {
    if (this.constructor.name === 'VariantRadios') return;

    selects.forEach(select => {
      const input = select.querySelector('select');
      const value = Array.from(newSelects).find(newSelect => {
        const newInput = newSelect.querySelector('select');

        return input.id === newInput.id;
      });

      if (value) select.innerHTML = value.innerHTML;
    });

    this.addFakeListener(selects);
  }

  toggleAddButton(disable = true, modifyClass = true, loading = false) {
    if (!this.productSection) return;

    const addButtons = this.productSection.querySelectorAll('.js-add-button');

    if (!addButtons.length) return;

    if (loading) return addButtons.forEach(button => button.classList.add('loading'));

    addButtons.forEach(button => {
      const addButtonText = button.querySelector('.button__label');

      if (addButtonText) {
        addButtonText.textContent = window.variantStrings[disable ? 'soldOut' : 'addToBag'];

        button.toggleAttribute('disabled', disable);
      }

      button.classList.remove('loading');
    });
  }

  setUnavailable() {
    if (!this.productSection) return;

    const addButtons = this.productSection.querySelectorAll('[name="add"]');
    const price = document.getElementById(`price-${this.dataset.section}`);
    const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
    const sku = document.getElementById(`Sku-${this.dataset.section}`);

    if (!addButtons.length) return;

    addButtons.forEach(button => {
      const addButtonText = button.querySelector('span');

      addButtonText.textContent = window.variantStrings.unavailable;
    });

    if (price) price.classList.add('visibility-hidden');
    if (inventory) inventory.classList.add('visibility-hidden');
    if (sku) sku.classList.add('visibility-hidden');
  }

  getVariantData() {
    this.variantData =
      this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);

    return this.variantData;
  }

  addFakeListener(element) {
    let selects = element || this.querySelectorAll('.select');

    selects.forEach(select => {
      const dropdown = select.querySelector('.select__dropdown');
      const options = select.querySelectorAll('.select__item');
      const label = select.querySelector('.select__label');

      if (label)
        label.addEventListener('click', event => {
          event.stopPropagation();

          select.classList.toggle('select--active');
        });

      options.forEach(option =>
        option.addEventListener('click', event => {
          this.updateSelect(event);

          select.classList.remove('select--active');
        })
      );

      window.addEventListener('click', event => {
        if (!select.contains(event.target) && select?.classList.contains('select--active')) {
          select.classList.remove('select--active');
        }
      });
    });
  }

  updateSelect(event) {
    const container = event.target.closest('.select');
    const select = container.querySelector('.select__select');
    const options = Array.from(select.querySelectorAll('option'));
    const selected = event.target.dataset.value;
    const selectedOption = options.find(option => option.value === selected);

    if (selectedOption) select.value = selectedOption.value;

    select.dispatchEvent(new Event('change', { bubbles: true }));
    this.updateVariantImage();
  }

  updateVariantImage() {
    const variantId = document.querySelector('[name="id"]').value;
    const imageElements = document.querySelectorAll('.product__figure[data-variant-id]');

    const matchingImage = Array.from(imageElements).find(el => {
      const ids = el.getAttribute('data-variant-id').split(',');
      return ids.includes(String(variantId));
    });

    if (matchingImage) {
      const mainImageContainer = document.querySelector('.product__figure--main .product__figure');

      // Swap innerHTML
      const currentImageHTML = mainImageContainer.innerHTML;
      mainImageContainer.innerHTML = matchingImage.innerHTML;
      matchingImage.innerHTML = currentImageHTML;

      // Swap data-variant-id
      const mainVariantId = mainImageContainer.getAttribute('data-variant-id');
      const matchingVariantId = matchingImage.getAttribute('data-variant-id');
      mainImageContainer.setAttribute('data-variant-id', matchingVariantId);
      matchingImage.setAttribute('data-variant-id', mainVariantId);
    }

    const carousel = document.getElementById('productMainSlider');

    if (carousel && typeof carousel.scrollToSlide === 'function') {
      const slides = carousel.querySelectorAll('.js-carousel-slide');

      const index = Array.from(slides).findIndex(slide => {
        const ids = slide.getAttribute('data-variant-id')?.split(',');
        return ids && ids.includes(variantId);
      });

      if (index !== -1) {
        carousel.scrollToSlide(index + 1);
      }
    }
  }
}

customElements.define('variant-selects', VariantSelects);

class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach(input => {
      const availableOption = listOfAvailableOptions.includes(input.getAttribute('value'));

      input.toggleAttribute('disabled', availableOption);
    });
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));

    this.options = fieldsets.map(fieldset => {
      return Array.from(fieldset.querySelectorAll('input')).find(radio => radio.checked).value;
    });
  }

  updateVariants() {
    super.updateVariants();

    publish(PUB_SUB_EVENTS.resetOptions);
  }
}

customElements.define('variant-radios', VariantRadios);

class ProductOptions extends HTMLElement {
  constructor() {
    super();

    this.options = this.querySelectorAll('.product-option');
    this.productForm = document.querySelector('.product-form');
    this.variantInputs = this.productForm.querySelectorAll('.product-variant-id');

    this.addClickListeners();

    subscribe(PUB_SUB_EVENTS.bundleUpdate, this.updateOption.bind(this));
    subscribe(PUB_SUB_EVENTS.resetOptions, this.resetOptions.bind(this));
  }

  addClickListeners() {
    this.options.forEach(option => {
      option.addEventListener('click', event => {
        event.stopPropagation();

        const input = option.querySelector('input');

        input.click();

        this.updateOptions(input.value);
        this.updatePrice(option);

        this.variantInputs.forEach(variantInput => (variantInput.value = input.value));
      });
    });
  }

  updateOptions(value) {
    this.options.forEach(option => {
      const input = option.querySelector('input');

      if (input.value !== value) input.checked = false;
    });
  }

  updatePrice(option) {
    const optionPrice = option.querySelector('.price-item');
    const quantityPrice = option
      .closest('.product-form')
      .querySelector('.product__quantity-price .price-item');
    const buttonsPrice = this.productForm.querySelectorAll('.product-form__submit .price-item');

    [quantityPrice, ...buttonsPrice].forEach(price => (price.innerHTML = optionPrice.innerHTML));
  }

  updateOption({ variant, element }) {
    const targetOption = element.closest('.product-option');

    this.options.forEach(option => {
      if (targetOption === option) {
        const input = option.querySelector('input');

        input.value = variant;
        input.click();
      }
    });
  }

  resetOptions() {
    const defaultOption = this.options[0].querySelector('input');

    defaultOption.checked = true;

    this.updateOptions(defaultOption.value);
  }
}

customElements.define('product-options', ProductOptions);

class CardVariantSelects extends VariantSelects {
  constructor() {
    super();

    this.productSection = this.closest('.card--product');
  }

  connectedCallback() {
    const variants = this.getVariantData();
    const cheapest = variants.filter(v => v.available).sort((a, b) => a.price - b.price)[0];
    if (!cheapest) return;

    this.querySelectorAll('.select').forEach((select, i) => {
      const nativeSelect = select.querySelector('.select__select');
      const labelSpan = select.querySelector('.select__label > span');
      const val = cheapest[`option${i + 1}`];

      if (nativeSelect && val) nativeSelect.value = val;
      if (labelSpan && val) labelSpan.textContent = val;
    });

    const form = document.getElementById(`product-form-${this.dataset.id}`);
    const input = form?.querySelector('input[name="id"]');

    if (Number(input?.value) === cheapest.id) return;

    if (input) input.value = cheapest.id;
    this.currentVariant = cheapest;
    this.renderProductInfo();
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.id}`);

    super.updateVariantInput(productForms);
  }

  updateOptions() {
    this.swatch = this.productSection.querySelector('input:checked');

    if (!this.swatch) return super.updateOptions();

    this.selectOption = this.productSection.querySelector('select');
    this.options = [];

    if (this.swatch) this.options.push(this.swatch.value);
    if (this.selectOption) this.options.push(this.selectOption.value);
  }

  renderProductInfo() {
    const requestedVariantId = this.currentVariant.id;
    const sectionId = this.dataset.section;

    fetch(`/products/${this.dataset.handle}?variant=${requestedVariantId}&section_id=${sectionId}`)
      .then(response => response.text())
      .then(responseText => {
        if (this.currentVariant.id !== requestedVariantId) return;

        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const section = document.getElementById(`card-product-${this.dataset.id}`);
        const newPrice = html.querySelector(`.price-${this.dataset.id}`);
        const prices = section.querySelectorAll(`.price-${this.dataset.id}`);
        const newSelects = html.querySelectorAll('.select');
        const selects = section.querySelectorAll('.select');
        const newMedia = html.querySelector('.card__media');
        const media = section.querySelector('.card__media');
        const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.dataset.id}`);
        const isDisabled = addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true;

        this.updatePrice(prices, newPrice);
        this.updateVariants(selects, newSelects);
        this.updateMedia(media, newMedia);
        this.toggleAddButton(isDisabled);
      });
  }

  updateMedia(media, newMedia) {
    if (media && newMedia) media.innerHTML = newMedia.innerHTML;
  }
}

customElements.define('card-variant-selects', CardVariantSelects);

class CardSwatches extends CardVariantSelects {
  constructor() {
    super();
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach(input => {
      const availableOption = listOfAvailableOptions.includes(input.getAttribute('value'));

      input.toggleAttribute('disabled', availableOption);
    });
  }
}

customElements.define('card-swatches', CardSwatches);
